import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@leagueplatform/genesis-core';
import {
  getPersistedEnvironmentKey,
  getPersistedMirrordRoute,
  getSwitcherEnvironments,
  persistEnvironmentKey,
  persistMirrordRoute,
  type EosEnvironmentKey,
} from 'common/environments';

const MONO =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

// Self-contained dark chrome for the dev-only overlay: it deliberately sits off
// the product's (light) Genesis theme so it reads as tooling, not app UI.
const T = {
  bg: '#1B1B1F',
  raised: '#27272D',
  raisedHover: '#2F2F36',
  border: 'rgba(255,255,255,0.10)',
  borderStrong: 'rgba(255,255,255,0.18)',
  text: '#ECECEE',
  muted: '#9A9AA3',
  faint: '#6E6E77',
  accent: '#6AA1FF',
  accentRing: 'rgba(106,161,255,0.28)',
  ok: '#46D17F',
  danger: '#FF6B6B',
  dangerWash: 'rgba(255,107,107,0.10)',
};

const Chevron = ({
  size = 11,
  color = T.muted,
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

interface EnvironmentSwitcherProps {
  /**
   * Routes where the switcher is hidden. A route hides its exact path and any
   * sub-path (e.g. '/benefits' also hides '/benefits/123'). Defaults to none,
   * so the switcher renders on every page.
   */
  excludedRoutes?: string[];
}

export const EnvironmentSwitcher = ({
  excludedRoutes = [],
}: EnvironmentSwitcherProps) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const activeKey = getPersistedEnvironmentKey();
  const activeMirrordRoute = getPersistedMirrordRoute();
  const [mirrordRouteDraft, setMirrordRouteDraft] =
    useState(activeMirrordRoute);
  const environments = getSwitcherEnvironments();
  const activeEnvironment =
    environments.find((environment) => environment.key === activeKey) ??
    environments[0];

  // Dev-only tool: never render in a production build — it would expose the
  // prod env selector / non-prod endpoints to end users. (Stays visible on dev
  // and the staging/UAT demo builds, where switching is the whole point.)
  if (import.meta.env.VITE_APP_ENV === 'production') {
    return null;
  }

  const isRouteExcluded = excludedRoutes.some(
    (route) =>
      location.pathname === route || location.pathname.startsWith(`${route}/`),
  );
  if (isRouteExcluded) {
    return null;
  }

  const handleEnvironmentChange = (nextKey: EosEnvironmentKey) => {
    if (nextKey === activeKey) {
      return;
    }

    persistEnvironmentKey(nextKey);
    window.location.reload();
  };

  const handleMirrordRouteSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextRoute = mirrordRouteDraft.trim();
    if (nextRoute === activeMirrordRoute) {
      return;
    }

    persistMirrordRoute(nextRoute);
    window.location.reload();
  };

  const isProd = activeEnvironment.key === 'prod';
  const dotColor = isProd ? T.danger : T.ok;
  const apiHost = activeEnvironment.apiUrl.replace(/^https?:\/\//, '');
  const routeUnchanged = mirrordRouteDraft.trim() === activeMirrordRoute;

  const fieldStyles = {
    width: '100%',
    boxSizing: 'border-box',
    height: '30px',
    padding: '0 10px',
    borderRadius: '6px',
    border: `1px solid ${T.border}`,
    backgroundColor: T.raised,
    color: T.text,
    colorScheme: 'dark',
    fontFamily: MONO,
    fontSize: '12px',
    lineHeight: '1',
    outline: 'none',
    transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
    '&:hover': { borderColor: T.borderStrong },
    '&:focus-visible': {
      borderColor: T.accent,
      boxShadow: `0 0 0 3px ${T.accentRing}`,
    },
  } as const;

  const labelStyles = {
    display: 'block',
    margin: '0 0 6px',
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: T.faint,
  } as const;

  return (
    <Box
      css={{
        position: 'fixed',
        left: '$one',
        // Sit above both the React Query devtools toggle (≈48px tall, also
        // bottom-left) and the demo chat's composer, which is full width and
        // pinned to the bottom — at 52px this panel would sit under it.
        bottom: '80px',
        zIndex: 9999,
        width: '256px',
        borderRadius: '10px',
        border: `1px solid ${T.border}`,
        backgroundColor: T.bg,
        color: T.text,
        boxShadow:
          '0 1px 2px rgba(0,0,0,0.30), 0 12px 28px -8px rgba(0,0,0,0.55)',
        overflow: 'hidden',
        pointerEvents: 'auto',
        fontFamily: MONO,
      }}
    >
      <Box
        as="button"
        type="button"
        aria-expanded={isOpen}
        aria-label="Toggle environment switcher"
        onClick={() => {
          setIsOpen((open) => {
            if (!open) {
              setMirrordRouteDraft(activeMirrordRoute);
            }
            return !open;
          });
        }}
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          height: '38px',
          padding: '0 12px',
          border: 'none',
          backgroundColor: isProd ? T.dangerWash : 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          gap: '8px',
          transition: 'background-color 0.12s ease',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `inset 0 0 0 2px ${T.accentRing}`,
          },
        }}
      >
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: 0,
          }}
        >
          <Box
            aria-hidden="true"
            css={{
              flexShrink: 0,
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              backgroundColor: dotColor,
              boxShadow: `0 0 0 3px ${
                isProd ? T.dangerWash : 'rgba(70,209,127,0.16)'
              }`,
            }}
          />
          <Box
            as="span"
            css={{
              fontFamily: MONO,
              fontSize: '12px',
              fontWeight: 600,
              color: isProd ? T.danger : T.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {activeEnvironment.label}
            {activeMirrordRoute && (
              <Box as="span" css={{ color: T.muted, fontWeight: 400 }}>
                {` · ${activeMirrordRoute}`}
              </Box>
            )}
          </Box>
        </Box>
        <Box
          aria-hidden="true"
          css={{
            display: 'flex',
            flexShrink: 0,
            transition: 'transform 0.18s ease',
            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        >
          <Chevron />
        </Box>
      </Box>

      {isOpen && (
        <Box css={{ padding: '12px', borderTop: `1px solid ${T.border}` }}>
          <Box as="label" css={{ display: 'block', marginBottom: '12px' }}>
            <Box as="span" css={labelStyles}>
              BE environment
            </Box>
            <Box css={{ position: 'relative' }}>
              <Box
                as="select"
                value={activeKey}
                onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                  handleEnvironmentChange(
                    event.currentTarget.value as EosEnvironmentKey,
                  )
                }
                css={{
                  ...fieldStyles,
                  appearance: 'none',
                  paddingRight: '28px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {environments.map((environment) => (
                  <option
                    key={environment.key || 'staging'}
                    value={environment.key}
                  >
                    {environment.label}
                  </option>
                ))}
              </Box>
              <Box
                aria-hidden="true"
                css={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Chevron />
              </Box>
            </Box>
            <Box
              as="span"
              css={{
                display: 'block',
                marginTop: '6px',
                fontFamily: MONO,
                fontSize: '11px',
                color: T.faint,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {apiHost}
            </Box>
          </Box>

          <Box as="form" onSubmit={handleMirrordRouteSubmit}>
            <Box as="label" htmlFor="eos-mirrord-route" css={labelStyles}>
              Mirrord route
            </Box>
            <Box
              as="input"
              id="eos-mirrord-route"
              name="mirrordRoute"
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              data-1p-ignore="true"
              data-lpignore="true"
              data-form-type="other"
              value={mirrordRouteDraft}
              placeholder="your-name"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setMirrordRouteDraft(event.currentTarget.value)
              }
              css={{
                ...fieldStyles,
                '&::placeholder': { color: T.faint },
              }}
            />
            <Box
              as="button"
              type="submit"
              disabled={routeUnchanged}
              css={{
                width: '100%',
                height: '30px',
                marginTop: '10px',
                borderRadius: '6px',
                border: 'none',
                fontFamily: MONO,
                fontSize: '12px',
                fontWeight: 600,
                cursor: routeUnchanged ? 'default' : 'pointer',
                backgroundColor: routeUnchanged
                  ? 'rgba(255,255,255,0.07)'
                  : T.text,
                color: routeUnchanged ? T.faint : T.bg,
                transition: 'background-color 0.12s ease',
                '&:hover': routeUnchanged ? {} : { backgroundColor: '#FFFFFF' },
                '&:focus-visible': {
                  outline: 'none',
                  boxShadow: `0 0 0 3px ${T.accentRing}`,
                },
              }}
            >
              Apply route
            </Box>
          </Box>

          <Box
            css={{
              marginTop: '12px',
              paddingTop: '10px',
              borderTop: `1px solid ${T.border}`,
              fontFamily: MONO,
              fontSize: '10px',
              color: T.faint,
            }}
          >
            ↻ changes reload the app
          </Box>
        </Box>
      )}
    </Box>
  );
};
